/*
 * @Author: dinggengren
 * @Date: 2026-04-17
 * @Company: www.smm.cn
 * @Description: 密码规则定义与 antd Form 校验器
 */

import { TranslateFunction } from "../../../Header/_components/HeaderContentWithScroll/types"

export type PasswordRule = {
  /** 规则唯一 key */
  key: string
  /** 浮窗里展示的规则文案 */
  label: string
  /** 实时判断是否通过 */
  check: (v: string) => boolean
  /** Form 校验失败时的错误提示，缺省回退到 label */
  message?: string
}

/**
 * 默认密码规则（统一内容，所有使用场景保持一致）
 * - 8-20 characters
 * - At least 3 of: uppercase, lowercase, numbers, symbols. No spaces.
 *
 * 依赖外部传入 `t`（next-intl 根作用域的翻译函数），
 * i18n 来自 shared messages: packages/i18n/src/locales/*.json 的 `passwordRules` 命名空间。
 */
export const defaultPasswordRules = (t: TranslateFunction): PasswordRule[] => [
  {
    key: "length",
    label: t("passwordRules.length"),
    message: t("passwordRules.length_error"),
    check: (v) => v.length >= 8 && v.length <= 20,
  },
  {
    key: "composition",
    label: t("passwordRules.composition"),
    check: (v) => {
      if (/\s/.test(v)) return false
      const kinds = [/[A-Z]/, /[a-z]/, /\d/, /[^A-Za-z0-9\s]/].filter((r) => r.test(v)).length
      return kinds >= 3
    },
  },
]

/**
 * 把密码规则转成 antd Form.Item 可用的 validator
 * 找到第一条未通过的规则，抛出它的 message / label 作为错误提示
 *
 * @example
 * ```tsx
 * const t = useTranslations()
 * <Form.Item rules={[{ validator: createPasswordValidator(t) }]}>...</Form.Item>
 * ```
 */
export const createPasswordValidator =
  (t: TranslateFunction) =>
  async (_rule: unknown, value: string): Promise<void> => {
    if (!value) return
    const failed = defaultPasswordRules(t).find((r) => !r.check(value))
    if (failed) {
      throw new Error(failed.message ?? failed.label)
    }
  }

/**
 * 判断所有规则是否都通过（便于业务层决定提交按钮禁用态等）
 */
export const isPasswordValid = (value: string, t: TranslateFunction): boolean =>
  !!value && defaultPasswordRules(t).every((r) => r.check(value))
